import { useMemo, useState, useEffect, useRef } from 'react';
import { View, Pressable, Platform, ActivityIndicator, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '@/theme';
import { Card } from '@/components/ui/Card';
import { ThemedText, Body, Caption } from '@/components/ui/Text';
import {
  formatCurrency,
  sumExpensesInTripBaseCurrency,
  expenseListHasForeignCurrency,
} from '@/utils/currency';
import { coerceDate } from '@/utils/dateKeys';
import type { Trip } from '@/services/trips';
import type { Expense } from '@/services/expenses';
import { DualCurrencyDisplay } from '@/components/expenses/DualCurrencyDisplay';

type ExpenseSummaryWidgetProps = {
  trip: Trip;
  expenses: Expense[];
  onViewAll?: () => void;
  onAddExpense?: () => void;
};

type FilterPeriod = 'today' | 'week' | 'month' | 'all';

export function ExpenseSummaryWidget({ 
  trip, 
  expenses, 
  onViewAll,
  onAddExpense 
}: ExpenseSummaryWidgetProps) {
  const theme = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  /** Stack title + actions on narrow widths so "Add expense" never clips past the card edge. */
  const compactActionsLayout = windowWidth < 420;
  const baseCurrency = trip.baseCurrency || 'USD';
  const [filterPeriod, setFilterPeriod] = useState<FilterPeriod>('week');
  
  // Track pending/processing receipts
  const { pendingReceipts, completedExpenses } = useMemo(() => {
    const pending = expenses.filter(e => e.status === 'pending');
    const completed = expenses.filter(e => e.status !== 'pending');
    return { pendingReceipts: pending, completedExpenses: completed };
  }, [expenses]);
  
  const hasExpenses = expenses.length > 0;
  const hasPendingReceipts = pendingReceipts.length > 0;

  const expenseBuckets = useMemo(() => {
    const now = new Date();
    const today = new Date(now);
    today.setHours(0, 0, 0, 0);

    const weekAgo = new Date(today);
    weekAgo.setDate(weekAgo.getDate() - 7);

    const monthAgo = new Date(today);
    monthAgo.setMonth(monthAgo.getMonth() - 1);

    const getDateString = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const todayString = getDateString(today);

    const tripStart = coerceDate(trip.startDate) ?? new Date();
    tripStart.setHours(0, 0, 0, 0);
    const tripEnd = coerceDate(trip.endDate) ?? new Date(tripStart);
    tripEnd.setHours(23, 59, 59, 999);

    const tripExpenses = expenses.filter((expense) => {
      const expenseDate = coerceDate(expense.date);
      if (!expenseDate) return false;
      const withinTrip = expenseDate >= tripStart && expenseDate <= tripEnd;
      const isTodayExpense = getDateString(expenseDate) === todayString;
      const isFutureTrip = tripStart > today;
      return isFutureTrip || withinTrip || isTodayExpense;
    });

    const todayMidnight = new Date(today);
    const daysElapsed = Math.max(
      1,
      Math.ceil((todayMidnight.getTime() - tripStart.getTime()) / (1000 * 60 * 60 * 24)),
    );
    const remainingDays = Math.max(
      0,
      Math.ceil((tripEnd.getTime() - todayMidnight.getTime()) / (1000 * 60 * 60 * 24)),
    );

    const weekAgoString = getDateString(weekAgo);
    const todayExpenses = tripExpenses.filter((expense) => {
      const expenseDate = coerceDate(expense.date);
      if (!expenseDate) return false;
      return getDateString(expenseDate) === todayString;
    });

    const weekExpenses = tripExpenses.filter((expense) => {
      const expenseDate = coerceDate(expense.date);
      if (!expenseDate) return false;
      const expenseDateString = getDateString(expenseDate);
      return expenseDateString >= weekAgoString && expenseDateString <= todayString;
    });

    const twoWeeksAgo = new Date(weekAgo);
    twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 7);
    const twoWeeksAgoString = getDateString(twoWeeksAgo);

    const previousWeekExpenses = tripExpenses.filter((expense) => {
      const expenseDate = coerceDate(expense.date);
      if (!expenseDate) return false;
      const expenseDateString = getDateString(expenseDate);
      return expenseDateString >= twoWeeksAgoString && expenseDateString < weekAgoString;
    });

    const sortedExpenses = [...tripExpenses].sort((a, b) => {
      const aDate = coerceDate(a.date);
      const bDate = coerceDate(b.date);
      return (bDate?.getTime() ?? 0) - (aDate?.getTime() ?? 0);
    });
    const lastExpense = sortedExpenses[0];

    const isFutureTrip = tripStart > today;

    let filteredExpenses = tripExpenses;
    if (isFutureTrip) {
      filteredExpenses = tripExpenses;
    } else if (filterPeriod === 'today') {
      filteredExpenses = tripExpenses.filter((expense) => {
        const expenseDate = coerceDate(expense.date);
        if (!expenseDate) return false;
        return getDateString(expenseDate) === todayString;
      });
    } else if (filterPeriod === 'week') {
      filteredExpenses = tripExpenses.filter((expense) => {
        const expenseDate = coerceDate(expense.date);
        if (!expenseDate) return false;
        const expenseDateString = getDateString(expenseDate);
        return expenseDateString >= weekAgoString && expenseDateString <= todayString;
      });
    } else if (filterPeriod === 'month') {
      const monthAgoString = getDateString(monthAgo);
      filteredExpenses = tripExpenses.filter((expense) => {
        const expenseDate = coerceDate(expense.date);
        if (!expenseDate) return false;
        const expenseDateString = getDateString(expenseDate);
        return expenseDateString >= monthAgoString && expenseDateString <= todayString;
      });
    }

    return {
      tripExpenses,
      filteredExpenses,
      todayExpenses,
      weekExpenses,
      previousWeekExpenses,
      lastExpense,
      daysElapsed,
      remainingDays,
      isFutureTrip,
      totalExpenses: tripExpenses.length,
    };
  }, [expenses, trip.startDate, trip.endDate, filterPeriod]);

  const [convertedTotals, setConvertedTotals] = useState({
    filteredSpending: 0,
    todaySpending: 0,
    weekSpending: 0,
    previousWeekSpending: 0,
    totalTripSpending: 0,
  });
  const [convertingTotals, setConvertingTotals] = useState(false);
  const convertVersionRef = useRef(0);

  useEffect(() => {
    const { tripExpenses, filteredExpenses, todayExpenses, weekExpenses, previousWeekExpenses } =
      expenseBuckets;
    const version = ++convertVersionRef.current;

    if (tripExpenses.length === 0) {
      setConvertedTotals({
        filteredSpending: 0,
        todaySpending: 0,
        weekSpending: 0,
        previousWeekSpending: 0,
        totalTripSpending: 0,
      });
      setConvertingTotals(false);
      return;
    }

    setConvertingTotals(true);
    (async () => {
      try {
        const [
          filteredSpending,
          todaySpending,
          weekSpending,
          previousWeekSpending,
          totalTripSpending,
        ] = await Promise.all([
          sumExpensesInTripBaseCurrency(filteredExpenses, baseCurrency),
          sumExpensesInTripBaseCurrency(todayExpenses, baseCurrency),
          sumExpensesInTripBaseCurrency(weekExpenses, baseCurrency),
          sumExpensesInTripBaseCurrency(previousWeekExpenses, baseCurrency),
          sumExpensesInTripBaseCurrency(tripExpenses, baseCurrency),
        ]);
        if (convertVersionRef.current !== version) return;
        setConvertedTotals({
          filteredSpending,
          todaySpending,
          weekSpending,
          previousWeekSpending,
          totalTripSpending,
        });
      } catch (error) {
        console.error('[ExpenseSummaryWidget] Failed to convert spending totals', error);
        if (convertVersionRef.current !== version) return;
        setConvertedTotals({
          filteredSpending: 0,
          todaySpending: 0,
          weekSpending: 0,
          previousWeekSpending: 0,
          totalTripSpending: 0,
        });
      } finally {
        if (convertVersionRef.current === version) {
          setConvertingTotals(false);
        }
      }
    })();
  }, [expenseBuckets, baseCurrency]);

  const metrics = useMemo(() => {
    const { daysElapsed, remainingDays, lastExpense, totalExpenses, filteredExpenses } =
      expenseBuckets;
    const { totalTripSpending, weekSpending, previousWeekSpending, filteredSpending } =
      convertedTotals;

    const dailyRate = daysElapsed > 0 ? totalTripSpending / daysElapsed : 0;
    const projectedSpending = dailyRate * (daysElapsed + remainingDays);
    const budget = trip.budget || 0;
    const projectedPercentage = budget > 0 ? (projectedSpending / budget) * 100 : 0;
    const velocityStatus =
      budget > 0
        ? projectedPercentage > 100
          ? 'over'
          : projectedPercentage > 90
            ? 'warning'
            : projectedPercentage > 75
              ? 'on-track'
              : 'under'
        : 'unknown';

    const trend =
      previousWeekSpending > 0
        ? ((weekSpending - previousWeekSpending) / previousWeekSpending) * 100
        : 0;

    const filteredHasForeign = expenseListHasForeignCurrency(filteredExpenses, baseCurrency);

    return {
      lastExpense,
      trend,
      totalExpenses,
      dailyRate,
      projectedSpending,
      velocityStatus,
      remainingDays,
      filteredSpending,
      filteredExpenses,
      filteredHasForeign,
    };
  }, [expenseBuckets, convertedTotals, trip.budget, baseCurrency]);

  // Check if trip is currently active
  const isActive = useMemo(() => {
    const now = new Date();
    const start = coerceDate(trip.startDate);
    const end = coerceDate(trip.endDate) ?? start;
    if (!start || !end) return false;
    return now >= start && now <= end;
  }, [trip.endDate, trip.startDate]);

  return (
    <Card variant="outlined" style={{ marginBottom: theme.spacing(2) }}>
      <View
        style={{
          flexDirection: compactActionsLayout ? 'column' : 'row',
          alignItems: compactActionsLayout ? 'stretch' : 'center',
          justifyContent: 'space-between',
          gap: theme.spacing(1.25),
          marginBottom: theme.spacing(1.25),
        }}
      >
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: theme.spacing(1),
            flex: compactActionsLayout ? undefined : 1,
            flexShrink: 1,
            minWidth: 0,
          }}
        >
          <Ionicons name="receipt-outline" size={18} color={theme.colors.primary} />
          <View style={{ flex: 1, minWidth: 0 }}>
            <Caption color={theme.colors.textSecondary} style={{ fontSize: 12, fontWeight: '600' }}>
              Spending
            </Caption>
            <Body color={theme.colors.textSecondary} style={{ fontSize: 11 }} numberOfLines={2}>
              Track expenses for this trip
            </Body>
          </View>
        </View>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
            gap: theme.spacing(1),
            flexShrink: 0,
            alignSelf: compactActionsLayout ? 'stretch' : undefined,
          }}
        >
          {onViewAll && hasExpenses && (
            <Pressable
              onPress={() => {
                // Set filter to 'all' to show all expenses immediately
                setFilterPeriod('all');
                // Also trigger the parent callback to expand the all expenses section
                onViewAll();
              }}
              style={({ pressed }) => ({
                paddingHorizontal: theme.spacing(1),
                paddingVertical: theme.spacing(0.75),
                borderRadius: theme.radii.md,
                opacity: pressed ? 0.7 : 1,
              })}
            >
              <Body color={theme.colors.primary} style={{ fontSize: 12, fontWeight: '600' }}>
                View all
              </Body>
            </Pressable>
          )}
          {onAddExpense && (
            <Pressable
              onPress={onAddExpense}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: theme.spacing(0.5),
                paddingHorizontal: compactActionsLayout ? theme.spacing(1.25) : theme.spacing(1.5),
                paddingVertical: theme.spacing(0.75),
                borderRadius: theme.radii.md,
                backgroundColor: theme.colors.primary,
                opacity: pressed ? 0.8 : 1,
                ...(Platform.OS === 'web'
                  ? { boxShadow: `0 4px 10px ${theme.colors.primary}33` }
                  : {
                      shadowColor: theme.colors.primary,
                      shadowOffset: { width: 0, height: 2 },
                      shadowOpacity: 0.2,
                      shadowRadius: 4,
                      elevation: 3,
                    }),
              })}
              accessibilityRole="button"
              accessibilityLabel="Add an expense"
            >
              <Ionicons
                name="add-circle"
                size={18}
                color={theme.colorScheme === 'dark' ? '#000000' : '#FFFFFF'}
              />
              <Body
                color={theme.colorScheme === 'dark' ? '#000000' : '#FFFFFF'}
                style={{ fontSize: compactActionsLayout ? 12 : 13, fontWeight: '700' }}
                numberOfLines={1}
              >
                {compactActionsLayout ? 'Add' : 'Add expense'}
              </Body>
            </Pressable>
          )}
        </View>
      </View>
      
      {hasExpenses && (
        <View style={{ 
          flexDirection: 'row', 
          gap: theme.spacing(0.5),
          marginBottom: theme.spacing(1.25),
        }}>
          {(['today', 'week', 'month', 'all'] as FilterPeriod[]).map((period) => (
            <Pressable
              key={period}
              onPress={() => setFilterPeriod(period)}
              style={({ pressed }) => ({
                flex: 1,
                paddingVertical: theme.spacing(0.75),
                paddingHorizontal: theme.spacing(1),
                borderRadius: theme.radii.md,
                backgroundColor: filterPeriod === period 
                  ? theme.colors.primary + '20' 
                  : theme.colors.borderMuted + '40',
                borderWidth: 1,
                borderColor: filterPeriod === period 
                  ? theme.colors.primary + '40'
                  : theme.colors.borderMuted,
                opacity: pressed ? 0.7 : 1,
              })}
            >
              <Caption 
                color={filterPeriod === period ? theme.colors.primary : theme.colors.textSecondary}
                style={{ 
                  fontSize: 10, 
                  fontWeight: filterPeriod === period ? '600' : '500',
                  textAlign: 'center',
                  textTransform: 'capitalize',
                }}
              >
                {period === 'all' ? 'All' : period}
              </Caption>
            </Pressable>
          ))}
        </View>
      )}

      {/* Pending Receipts Indicator */}
      {hasPendingReceipts && (
        <View style={{
          flexDirection: 'row',
          alignItems: 'center',
          gap: theme.spacing(1),
          paddingVertical: theme.spacing(1),
          paddingHorizontal: theme.spacing(1.5),
          backgroundColor: theme.colors.warning + '15',
          borderRadius: theme.radii.md,
          marginBottom: theme.spacing(1),
          borderWidth: 1,
          borderColor: theme.colors.warning + '30',
        }}>
          <View style={{
            width: 8,
            height: 8,
            borderRadius: 4,
            backgroundColor: theme.colors.warning,
          }} />
          <Caption color={theme.colors.warning} style={{ flex: 1, fontWeight: '500' }}>
            {pendingReceipts.length} receipt{pendingReceipts.length > 1 ? 's' : ''} processing...
          </Caption>
          <Caption color={theme.colors.textSecondary} style={{ fontSize: 10 }}>
            OCR in progress
          </Caption>
        </View>
      )}

      {hasExpenses ? (
        <View style={{ gap: theme.spacing(1.5) }}>
          {/* Metrics Row */}
          <View style={{ flexDirection: 'row', gap: theme.spacing(2) }}>
            {/* Filtered Spending */}
            <View style={{ flex: 1 }}>
              <Caption color={theme.colors.textSecondary} style={{ fontSize: 10, marginBottom: 2 }}>
                {filterPeriod === 'today' ? 'Today' : filterPeriod === 'week' ? 'This Week' : filterPeriod === 'month' ? 'This Month' : 'Total'}
              </Caption>
              {convertingTotals ? (
                <ActivityIndicator size="small" color={theme.colors.primary} style={{ alignSelf: 'flex-start' }} />
              ) : (
                <ThemedText variant="bodyStrong" style={{ fontSize: 18, fontWeight: '700' }}>
                  {formatCurrency(metrics.filteredSpending, baseCurrency)}
                </ThemedText>
              )}
              {metrics.filteredHasForeign && !convertingTotals && (
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 9, marginTop: 4, lineHeight: 13 }}>
                  Total in {baseCurrency}: foreign amounts use each expense&apos;s saved rate when available,
                  otherwise the app&apos;s current rate.
                </Caption>
              )}
              {metrics.trend !== 0 && filterPeriod === 'week' && !convertingTotals && (
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 2, marginTop: 2 }}>
                  <Ionicons 
                    name={metrics.trend > 0 ? "arrow-up" : "arrow-down"} 
                    size={10} 
                    color={metrics.trend > 0 ? theme.colors.error : theme.colors.success} 
                  />
                  <Caption 
                    color={metrics.trend > 0 ? theme.colors.error : theme.colors.success} 
                    style={{ fontSize: 9, fontWeight: '600' }}
                  >
                    {Math.abs(Math.round(metrics.trend))}%
                  </Caption>
                </View>
              )}
            </View>

            {/* Spending Velocity Indicator */}
            {isActive && metrics.dailyRate > 0 && !convertingTotals && (
              <View style={{ flex: 1, alignItems: 'flex-end' }}>
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 10, marginBottom: 2 }}>
                  Velocity
                </Caption>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(0.5) }}>
                  <Ionicons 
                    name={
                      metrics.velocityStatus === 'over' ? 'warning' :
                      metrics.velocityStatus === 'warning' ? 'alert-circle' :
                      metrics.velocityStatus === 'on-track' ? 'checkmark-circle' :
                      'trending-down'
                    }
                    size={14}
                    color={
                      metrics.velocityStatus === 'over' ? theme.colors.error :
                      metrics.velocityStatus === 'warning' ? theme.colors.warning :
                      metrics.velocityStatus === 'on-track' ? theme.colors.success :
                      theme.colors.textSecondary
                    }
                  />
                  <ThemedText 
                    variant="bodyStrong" 
                    style={{ 
                      fontSize: 12,
                      color: metrics.velocityStatus === 'over' ? theme.colors.error :
                             metrics.velocityStatus === 'warning' ? theme.colors.warning :
                             theme.colors.success
                    }}
                  >
                    {metrics.velocityStatus === 'over' ? 'Over' :
                     metrics.velocityStatus === 'warning' ? 'Fast' :
                     metrics.velocityStatus === 'on-track' ? 'On Track' :
                     'Under'}
                  </ThemedText>
                </View>
              </View>
            )}
          </View>
          
          {/* Predictive Text */}
          {isActive && metrics.dailyRate > 0 && metrics.remainingDays > 0 && !convertingTotals && (
            <View style={{
              padding: theme.spacing(1),
              borderRadius: theme.radii.md,
              backgroundColor: theme.colors.borderMuted + '30',
              flexDirection: 'row',
              alignItems: 'center',
              gap: theme.spacing(1),
            }}>
              <Ionicons name="bulb-outline" size={14} color={theme.colors.textSecondary} />
              <View style={{ flex: 1 }}>
                <Caption color={theme.colors.textSecondary} style={{ fontSize: 10 }}>
                  At this rate, you'll spend{' '}
                  <ThemedText variant="caption" style={{ fontWeight: '600', color: theme.colors.text }}>
                    {formatCurrency(metrics.projectedSpending, baseCurrency)}
                  </ThemedText>
                  {' '}by trip end
                </Caption>
              </View>
            </View>
          )}

          {/* Last Expense */}
          {metrics.lastExpense && (
            <View style={{ 
              flexDirection: 'row', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              paddingTop: theme.spacing(1),
              borderTopWidth: 1,
              borderTopColor: theme.colors.borderMuted,
            }}>
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: theme.spacing(1), flex: 1 }}>
                <Ionicons name="time-outline" size={14} color={theme.colors.textSecondary} />
                <View style={{ flex: 1 }}>
                  <Body style={{ fontSize: 12 }} numberOfLines={1}>
                    {metrics.lastExpense.description || 'Expense'}
                  </Body>
                  <Caption color={theme.colors.textSecondary} style={{ fontSize: 10 }}>
                    {(() => {
                      const expenseDate = coerceDate(metrics.lastExpense.date);
                      if (!expenseDate) return 'Date TBD';
                      const now = new Date();
                      const diffMs = now.getTime() - expenseDate.getTime();
                      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
                      const diffDays = Math.floor(diffHours / 24);
                      
                      if (diffHours < 1) return 'Just now';
                      if (diffHours < 24) return `${diffHours}h ago`;
                      if (diffDays === 1) return 'Yesterday';
                      if (diffDays < 7) return `${diffDays}d ago`;
                      return expenseDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    })()}
                  </Caption>
                </View>
              </View>
              <DualCurrencyDisplay
                amount={metrics.lastExpense.amount}
                currency={metrics.lastExpense.currency}
                baseCurrency={baseCurrency}
                showRate
                size="small"
                savedConversion={
                  metrics.lastExpense.conversion &&
                  metrics.lastExpense.conversion.toCurrency.toUpperCase() === baseCurrency.toUpperCase()
                    ? {
                        convertedAmount: metrics.lastExpense.conversion.convertedAmount,
                        exchangeRate: metrics.lastExpense.conversion.exchangeRate,
                        toCurrency: metrics.lastExpense.conversion.toCurrency,
                      }
                    : undefined
                }
              />
            </View>
          )}
        </View>
      ) : (
        <View style={{ 
          alignItems: 'center', 
          paddingVertical: theme.spacing(2.5), 
          gap: theme.spacing(1) 
        }}>
          <Ionicons name="receipt-outline" size={26} color={theme.colors.textSecondary} />
          <Body color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
            No expenses yet. Add one to start tracking.
          </Body>
          <Caption color={theme.colors.textSecondary} style={{ textAlign: 'center' }}>
            Upload receipts or log spending to see insights here.
          </Caption>
        </View>
      )}
    </Card>
  );
}
